/**
 * Base Authentication Provider
 * Provides common storage functionality for all auth providers
 */

import type { UserContext } from '../types';

export abstract class BaseAuthProvider {
  protected readonly STORAGE_KEY = 'auth_user';
  protected readonly TOKEN_KEY = 'auth_token';
  protected readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';

  /**
   * Get stored user from localStorage
   */
  protected getStoredUser(): UserContext | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      return JSON.parse(stored) as UserContext;
    } catch (error) {
      console.error('Failed to get stored user:', error);
      return null;
    }
  }

  /**
   * Store user data in localStorage
   */
  protected storeUser(user: UserContext): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Failed to store user:', error);
    }
  }

  /**
   * Get stored access token
   */
  protected getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get stored token:', error);
      return null;
    }
  }

  /**
   * Get stored refresh token
   */
  protected getStoredRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get stored refresh token:', error);
      return null;
    }
  }

  /**
   * Store authentication tokens
   */
  protected storeTokens(accessToken: string, refreshToken: string): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(this.TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  /**
   * Clear all stored data
   */
  protected clearStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to clear storage:', error);
    }
  }
}
